import type { ScenarioRunSetModel, ValueFilterRule } from '@/shared/generated'
import type { ScenarioRunSetRow } from '../model/types'

import React, { useMemo } from 'react'
import { map, pipe, prop, uniq } from 'lodash/fp'

import { useScenariosRunsSetsQuery } from '@/entities/scenario'

import { QuickFiltersBar } from '@/widgets/quick-filters-bar'

import {
	MasterTable,
	SRT_TablePagination,
} from '@/shared/components/master-table'
import { Content, Footer, Header, Layout } from '@/shared/components/ui/layout'

import { useWhatifRunsQueryFilters } from '../lib/useWhatifRunsQueryFilters'
import { useWhatifRunsQueryPagination } from '../lib/useWhatifRunsQueryPagination'
import { useWhatifRunsRowAction } from '../lib/useWhatifRunsRowAction'
import { useWhatifRunsTable } from '../lib/useWhatifRunsTable'
import { useWhatifRunsTableColumns } from '../lib/useWhatifRunsTableColumns'

const transformToTableRows = (
	scenarioRunSet: ScenarioRunSetModel
): ScenarioRunSetRow => ({
	id: scenarioRunSet.id,
	created_at: scenarioRunSet.created_at,
	created_by: scenarioRunSet.created_by,
	combinations: pipe([map(prop('combination_id')), uniq])(
		scenarioRunSet.scenario_runs
	),
	optimization_groups: pipe([map(prop('optimization_group_id')), uniq])(
		scenarioRunSet.scenario_runs
	),
	scenarios: pipe([map(prop('scenario_id')), uniq])(
		scenarioRunSet.scenario_runs
	),
})
export function WhatifRunsPage() {
	const columns = useWhatifRunsTableColumns()
	const queryPagination = useWhatifRunsQueryPagination()
	const queryFilters = useWhatifRunsQueryFilters()

	const scenariosRunsQuery = useScenariosRunsSetsQuery({
		filters: queryFilters.filters,
		limit: queryPagination.pagination.pageSize,
		offset:
			queryPagination.pagination.pageIndex *
			queryPagination.pagination.pageSize,
	})
	const data = useMemo(
		() =>
			scenariosRunsQuery.data
				? map(transformToTableRows, scenariosRunsQuery.data.data)
				: [],
		[scenariosRunsQuery.data]
	)
	const renderRowActionMenuItems = useWhatifRunsRowAction()
	const table = useWhatifRunsTable({
		columns,
		data,
		isLoading: scenariosRunsQuery.isLoading,
		rowCount: scenariosRunsQuery.data?.total ?? 0,
		renderRowActionMenuItems,
		...queryPagination,
	})

	return (
		<Layout className='flex h-full flex-1 flex-col p-4'>
			<Header className='pb-2'>
				<QuickFiltersBar
					irremovableKeys={[
						'created_at',
						'created_by',
						'optimization_group_ids',
					]}
					rules={queryFilters.filters}
					fieldsConfig={queryFilters.fieldsConfig}
					onApply={(rules) => {
						queryPagination.onPaginationChange((state) => ({
							...state,
							pageIndex: 0,
						}))
						queryFilters.setFilters(rules as ValueFilterRule[])
					}}
				/>
			</Header>
			<Content className='border-base mb-4 flex flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
				<MasterTable table={table} />
			</Content>
			<Footer>
				<SRT_TablePagination table={table} pageSizeOptions={[2, 5, 10]} />
			</Footer>
		</Layout>
	)
}
